/**
 * The geometry both sides speak: the window into the sensor, and the homographies that
 * carry it.
 *
 * A {@link SourceWindow} is what the view frames and what the scanner samples — the one
 * object that crosses the seam between them, inside every `framing` and every `frame`.
 * The view builds it (`view/view-control.ts` holds the pan, zoom, rotation and desk-view
 * state that gestures move) and the scanner consumes it (the GL letterbox folds its map
 * into the matrix it already had; the warp composes it into the card's own homography).
 * Everything downstream of those samplers works in *window pixels* — a space aligned
 * with the screen whatever the angle — which is also why lifting a quad onto the overlay
 * is one scale per axis.
 *
 * Rotation is continuous, so the window is a *rotated* rectangle in sensor space and a
 * plain source rect cannot describe it. A SourceWindow therefore carries an affine map
 * from window pixels to sensor pixels. The keystone is the desk-view correction: a
 * camera looking down at the desk at an angle sees the far edge compressed, and mapping
 * the view rectangle onto a trapezoid — top corners inset by the strength fraction —
 * through a homography undoes exactly the perspective of a flat ground plane, the
 * reciprocal-depth vertical stretch included. That makes the full window→sensor map
 * *projective*, which only the GL sampler can honour (its shader already divides);
 * {@link windowHomography} builds it. The affine part is kept alongside because the
 * preview and the 2D fallbacks are plain-canvas consumers: the preview paints the affine
 * window and lets CSS apply the keystone on the way to the screen, so the compositor
 * pays for the warp rather than a second video upload.
 *
 * Pure functions and plain data. No state, no DOM, no side of the seam.
 */

/** A row-major 3x3 homography, as nine numbers. */
export type Homography = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

/**
 * The part of the sensor frame on screen: an sw x sh window of sensor pixels. `affine`
 * — row-major [a, b, tx, c, d, ty], a pure rotation plus translation — takes flat
 * window coordinates to sensor coordinates; `keystone` is the desk-view strength, the
 * fraction of the top edge inset at each corner. The full projective map for consumers
 * that can honour it comes from `windowHomography`.
 */
export interface SourceWindow {
  sw: number;
  sh: number;
  affine: readonly [number, number, number, number, number, number];
  keystone: number;
  /**
   * The view height the keystone trapezoid is fitted to. The scan window is the same
   * window with `sh` trimmed under the UI strip, and the trapezoid must not re-fit to
   * the trimmed height — that would hand the detector a different warp than the one on
   * screen. Equal to `sh` everywhere else.
   */
  keystoneSpan: number;
}

/**
 * A sub-rectangle of a window's *view* space, in window pixels — the space the quads
 * come back in, spanning `sw` across and `keystoneSpan` down.
 */
export interface ViewRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * How far past its drawn edge the detector is actually shown, per side, as a fraction of
 * the view.
 *
 * The frame is an aiming aid, not a guillotine: a card lined up against the outline has
 * its corners *on* the line, and a detector fed exactly that rectangle sees them touching
 * the raster's border — where the pose head is at its least certain and a hair of hand
 * movement pushes a corner out of frame entirely. So the drawn rectangle is the promise
 * and the scanned one is that plus a margin, which costs a few percent of the raster and
 * buys every quad a little room to sit in.
 */
export const CROP_MARGIN = 0.03;
/** Grow detection by 20% per dimension without changing the native preview. */
export const DETECTION_OVERSCAN = 1.2;
/** Top corners inset past 45% each leave under a tenth of the sensor row — noise. */
export const MAX_KEYSTONE = 0.45;

export function clamp(value: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, value));
}

/** Row-major 3x3 product a·b. */
function mul3(a: Homography, b: Homography): Homography {
  const out = new Array<number>(9);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      out[r * 3 + c] = a[r * 3] * b[c] + a[r * 3 + 1] * b[3 + c] + a[r * 3 + 2] * b[6 + c];
    }
  }
  return out as unknown as Homography;
}

/** Apply a row-major homography to a point, projective divide included. */
export function applyHomography(h: Homography, x: number, y: number): [number, number] {
  const w = h[6] * x + h[7] * y + h[8];
  return [(h[0] * x + h[1] * y + h[2]) / w, (h[3] * x + h[4] * y + h[5]) / w];
}

/** Inverse via the adjugate; homographies are scale-free so no determinant divide. */
export function invertHomography(h: Homography): Homography {
  return [
    h[4] * h[8] - h[5] * h[7],
    h[2] * h[7] - h[1] * h[8],
    h[1] * h[5] - h[2] * h[4],
    h[5] * h[6] - h[3] * h[8],
    h[0] * h[8] - h[2] * h[6],
    h[2] * h[3] - h[0] * h[5],
    h[3] * h[7] - h[4] * h[6],
    h[1] * h[6] - h[0] * h[7],
    h[0] * h[4] - h[1] * h[3],
  ];
}

/**
 * How far the centred trapezoid's magnified edge overhangs the window rect, as a
 * fraction of the rect's width, on each side.
 */
export function keystoneOverhang(m: number): number {
  const n = Math.abs(m);
  return n / (2 * (1 - n));
}

/**
 * The keystone on the unit square: the (0,0)-(1,1) view onto the trapezoid whose *top*
 * edge narrows for positive `m` and whose *bottom* edge narrows for negative — the
 * corrections for a camera looking down across the desk and up at it, respectively.
 * Derived in closed form from the four correspondences (the negative branch is the
 * positive one conjugated by the y-flip); the projective bottom row is what produces
 * the ground plane's reciprocal-depth vertical compression, the physically right one.
 *
 * The trapezoid is *centred*: its mid-row keeps the rect's full width and position, so
 * dragging the slider reads as the image tilting about its middle rather than zooming
 * out of one pinned edge — the edge-anchored variant felt like pan-plus-zoom. The cost
 * is that the magnified edge overhangs the rect by `keystoneOverhang` per side, which
 * the window fit accounts for and the preview paints. Centring is a pure horizontal
 * scale on the edge-anchored map, so it is the same rectification family — it moves no
 * vanishing point, and the aspect residual it adds is absorbed by the calibrated
 * stretch like every other similarity mismatch.
 */
export function keystoneUnit(m: number): Homography {
  if (!m) return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  const n = Math.abs(m);
  const spread = 1 / (1 - n);
  const off = (1 - spread) / 2;
  if (m < 0) {
    return [(1 - 2 * n) * spread, n, (1 - 2 * n) * off, 0, 1, 0, 0, 2 * n, 1 - 2 * n];
  }
  return [(1 - 2 * m) * spread, -m, (1 - 2 * m) * off + m, 0, 1 - 2 * m, 0, 0, -2 * m, 1];
}

/**
 * The display-side keystone for the preview, in unit coordinates of the *painted*
 * canvas: the canvas holds the window widened by the overhang on both sides (the
 * centred trapezoid samples beyond the rect at its magnified edge), so the map to the
 * screen is the keystone's inverse composed with that widening.
 */
export function keystoneDisplayUnit(m: number): Homography {
  const e = keystoneOverhang(m);
  const widen: Homography = [1 + 2 * e, 0, -e, 0, 1, 0, 0, 0, 1];
  return mul3(invertHomography(keystoneUnit(m)), widen);
}

/** Conjugate a unit-square homography into a w x h pixel space. */
export function conjugateHomography(h: Homography, w: number, height: number): Homography {
  return [
    h[0],
    (h[1] * w) / height,
    h[2] * w,
    (h[3] * height) / w,
    h[4],
    h[5] * height,
    h[6] / w,
    h[7] / height,
    h[8],
  ];
}

/** The keystone in window pixels: view point → flat-window point. Identity at 0. */
export function keystonePx(window: SourceWindow): Homography {
  return conjugateHomography(keystoneUnit(window.keystone), window.sw, window.keystoneSpan);
}

/**
 * The full projective window→sensor map: the keystone into the flat window, then the
 * window's rotation and placement into the sensor. What the GL sampler consumes.
 */
export function windowHomography(window: SourceWindow): Homography {
  const [a, b, tx, c, d, ty] = window.affine;
  const affine3: Homography = [a, b, tx, c, d, ty, 0, 0, 1];
  if (!window.keystone) return affine3;
  return mul3(affine3, keystonePx(window));
}

/** Whether two windows sample the same sensor region the same way. */
export function sameWindow(a: SourceWindow, b: SourceWindow): boolean {
  if (a.sw !== b.sw || a.sh !== b.sh) return false;
  if (a.keystone !== b.keystone || a.keystoneSpan !== b.keystoneSpan) return false;
  for (let i = 0; i < 6; i++) if (a.affine[i] !== b.affine[i]) return false;
  return true;
}

export function sameRect(a: ViewRect, b: ViewRect): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/**
 * Expand the detector's rectangle into sensor pixels hidden by aspect-fill. Keep its
 * coordinates in the preview's window space, so a negative origin still projects exactly.
 * A rotated view is bounded by intersecting the expansion with all four sensor edges.
 * Projective desk views keep their existing area until a separate fit is validated.
 */
export function overscanArea(
  window: SourceWindow,
  area: ViewRect,
  sensorWidth: number,
  sensorHeight: number,
): ViewRect {
  if (window.keystone || area.w <= 0 || area.h <= 0) return area;
  const [a, b, tx, c, d, ty] = window.affine;
  const cx = area.x + area.w / 2;
  const cy = area.y + area.h / 2;
  const px = a * cx + b * cy + tx;
  const py = c * cx + d * cy + ty;
  const roomX = Math.max(0, Math.min(px, sensorWidth - px));
  const roomY = Math.max(0, Math.min(py, sensorHeight - py));
  let halfW = (area.w * DETECTION_OVERSCAN) / 2;
  let halfH = (area.h * DETECTION_OVERSCAN) / 2;
  // Independent clamping preserves horizontal overscan when the full sensor height
  // is already visible (the common portrait phone case).
  if (Math.abs(b) < 1e-8 && Math.abs(c) < 1e-8) {
    halfW = Math.min(halfW, roomX);
    halfH = Math.min(halfH, roomY);
  } else if (Math.abs(a) < 1e-8 && Math.abs(d) < 1e-8) {
    halfW = Math.min(halfW, roomY);
    halfH = Math.min(halfH, roomX);
  } else {
    const fit = Math.min(
      1,
      roomX / (Math.abs(a) * halfW + Math.abs(b) * halfH),
      roomY / (Math.abs(c) * halfW + Math.abs(d) * halfH),
    );
    halfW *= fit;
    halfH *= fit;
  }
  return { x: cx - halfW, y: cy - halfH, w: halfW * 2, h: halfH * 2 };
}
